import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
  withHooks,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Subscription } from 'rxjs';
import { pipe, tap, switchMap, of, from, catchError } from 'rxjs';
import {
  Analysis,
  AnalysisPlanning,
  AnalysisPolygon,
  FileOverviewItem,
  normalizeTechnicalState,
} from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';
import { environment } from '@env/environment';

// Import slice creators
// import { createAnalysisDetailSlice } from './slices/analysis-detail.slice';
// import { createAnalysisFilesSlice } from './slices/analysis-files.slice';
// import { createAnalysisPolygonsSlice } from './slices/analysis-polygons.slice';
// import { createAnalysisPlanningsSlice } from './slices/analysis-plannings.slice';

// Import services
import { AnalysisCacheService } from './services/analysis-cache.service';

// Import models
import {
  InvalidationRequest,
  InvalidationReason,
  DEFAULT_CACHE_TTL,
} from './models/analysis-store.model';
const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

/** Returns true while the SI pipeline is actively processing */
function isTechnicallyActive(analysis: Analysis | null): boolean {
  if (!analysis) return false;
  const state = normalizeTechnicalState(analysis.technicalStatus?.state);
  return state === 'queued' || state === 'processing';
}

/** Returns true if task registered but state not yet settled */
function isPendingActivation(analysis: Analysis | null): boolean {
  if (!analysis) return false;
  const status = analysis.technicalStatus;
  if (!status) return false;
  const task = Number(status.task ?? 0);
  const state = status.state;
  return task > 0 && (!state || state === 'unknown' || state === 'draft');
}

/**
 * Analysis Detail Store — Feature Hub
 *
 * Composes all data section slices (detail, files, polygons, plannings)
 * and coordinates cascade invalidation for real-time updates.
 *
 * Public API is backward-compatible with the original 880-line store.
 */
export const AnalysisDetailStore = signalStore(
  { providedIn: 'root' },

  // System state (not part of slices)
  withState({
    lastSyncAt: null as string | null,
    polling: false,
    sseContext: null as { organizationId: string; analysisId: string } | null,
    pageVisible: true,
    error: null as string | null,
    lastLoadedId: null as string | null,
    selectedMediaId: undefined as string | undefined,
    selectedMediaType: undefined as '2d' | '3d' | undefined,

    // Chart data state (from old store)
    planningChartDataCache: {} as Record<string, any>,
    loadingChartData: false,

    // Data signals (owned by store)
    detail: null as Analysis | null,
    filesOverview: [] as FileOverviewItem[],
    plannings: [] as AnalysisPlanning[],
    polygons: null as AnalysisPolygon[] | null,

    // Loading states
    loadingDetail: false,
    loadingFiles: false,
    loadingPlannings: false,
    loadingPolygons: false,
  }),

  // Computed selectors (system + slice delegates)
  withComputed((state) => {
    return {
      // System state selectors
      selectLastSyncAt: computed(() => state.lastSyncAt()),
      selectPolling: computed(() => state.polling()),
      selectPageVisible: computed(() => state.pageVisible()),
      selectError: computed(() => state.error()),

      // Data selectors
      selectDetail: computed(() => state.detail()),
      selectPolygons: computed(() => state.polygons()),
      selectPlannings: computed(() => state.plannings()),
      selectFilesOverview: computed(() => state.filesOverview()),

      // Loading states
      selectLoadingStates: computed(() => ({
        detail: state.loadingDetail(),
        files: state.loadingFiles(),
        plannings: state.loadingPlannings(),
        polygons: state.loadingPolygons(),
        any:
          state.loadingDetail() ||
          state.loadingFiles() ||
          state.loadingPlannings() ||
          state.loadingPolygons(),
      })),

      // Backward compatibility selectors
      currentAnalysis: computed(() => state.detail()),
      filesOverview: computed(() => state.filesOverview() || []),
      isLoading: computed(() =>
        state.loadingDetail() ||
        state.loadingFiles() ||
        state.loadingPlannings() ||
        state.loadingPolygons()
      ),
      hasError: computed(() => !!state.error()),
      hasAnalysis: computed(() => !!state.detail()),
      analysisId: computed(() => state.detail()?.analysisId || null),
      isActive: computed(() => isTechnicallyActive(state.detail())),
      isComplete: computed(() =>
        state.detail()?.technicalStatus?.stage === 'complete'
      ),
      error: computed(() => state.error()), // Alias for selectError

      // Chart data selectors (from old store)
      planningChartDataCache: computed(() => state.planningChartDataCache()),
      loadingChartData: computed(() => state.loadingChartData()),
    };
  }),

  withMethods(
    (
      store,
      analysisService = inject(AnalysisService),
      toastService = inject(ToastService),
      cacheService = inject(AnalysisCacheService),
    ) => {
      // SSE event handler reference
      let sseSubscription: Subscription | null = null;

      /**
       * Load detail section with optional cache bypass
       */
      const loadDetailSection = rxMethod<{
        organizationId: string;
        analysisId: string;
        forceRefresh?: boolean;
      }>(
        pipe(
          tap(() => {
            patchState(store, { loadingDetail: store.detail() === null, error: null });
          }),
          switchMap(({ organizationId, analysisId, forceRefresh }) => {
            const cacheKey = `${organizationId}:${analysisId}`;

            // Check cache if not forcing refresh
            if (!forceRefresh) {
              const cached = cacheService.getOrNull<Analysis>(
                'detail',
                cacheKey,
              );
              if (cached) {
                patchState(store, { detail: cached, loadingDetail: false });
                return of(cached);
              }
            }

            // Fetch from API
            return from(
              analysisService.fetchAnalysisDetail(organizationId, analysisId, {
                sync: forceRefresh,
              }),
            ).pipe(
              tap((analysis) => {
                // Cache the result
                cacheService.set('detail', cacheKey, analysis, DEFAULT_CACHE_TTL);

                patchState(store, { detail: analysis, loadingDetail: false });
                debugLog(
                  `[AnalysisDetailStore] Detail loaded for ${analysisId}`,
                );
              }),
              catchError((err) => {
                const errorMessage =
                  err.message || 'Fehler beim Laden der Analyse-Details';
                debugLog(
                  '[AnalysisDetailStore] Error loading detail:',
                  err,
                );
                patchState(store, { error: errorMessage, loadingDetail: false });
                return of(null);
              }),
            );
          }),
        ),
      );

      /**
       * Load files overview with optional cache bypass
       */
      const loadFilesSection = rxMethod<{
        organizationId: string;
        analysisId: string;
        forceRefresh?: boolean;
      }>(
        pipe(
          tap(() => {
            patchState(store, { loadingFiles: store.filesOverview().length === 0, error: null });
          }),
          switchMap(({ organizationId, analysisId, forceRefresh }) => {
            const cacheKey = `${organizationId}:${analysisId}`;

            // Check cache if not forcing refresh
            if (!forceRefresh) {
              const cached = cacheService.getOrNull<FileOverviewItem[]>(
                'files',
                cacheKey,
              );
              if (cached) {
                patchState(store, { filesOverview: cached, loadingFiles: false });
                return of(cached);
              }
            }

            // Fetch from API
            return from(
              analysisService.getFilesOverview(organizationId, analysisId),
            ).pipe(
              tap((overview) => {
                // Cache the result
                cacheService.set(
                  'files',
                  cacheKey,
                  overview,
                  DEFAULT_CACHE_TTL - 30_000,
                );

                patchState(store, { filesOverview: overview, loadingFiles: false });
                debugLog(
                  `[AnalysisDetailStore] Files loaded: ${overview.length} items`,
                );
              }),
              catchError((err) => {
                const errorMessage =
                  err.message ||
                  'Fehler beim Laden der Dateiübersicht';
                debugLog('[AnalysisDetailStore] Error loading files:', err);
                patchState(store, { error: errorMessage, loadingFiles: false });
                toastService.error(errorMessage);
                return of(null);
              }),
            );
          }),
        ),
      );

      /**
       * Load plannings with optional cache bypass
       */
      const loadPlanningsSection = rxMethod<{
        organizationId: string;
        analysisId: string;
        forceRefresh?: boolean;
      }>(
        pipe(
          tap(() => {
            patchState(store, { loadingPlannings: store.plannings().length === 0, error: null });
          }),
          switchMap(({ organizationId, analysisId, forceRefresh }) => {
            const cacheKey = `${organizationId}:${analysisId}`;

            // Check cache if not forcing refresh
            if (!forceRefresh) {
              const cached = cacheService.getOrNull<AnalysisPlanning[]>(
                'plannings',
                cacheKey,
              );
              // Only use cache when module data is complete (name populated).
              // Stale entries from before the userId fix have module objects
              // with only id/solarInsightModuleId and no name/power.
              const cacheComplete = cached?.every(
                (p) => !p.module || p.module.name != null,
              ) ?? false;
              if (cached && cacheComplete) {
                patchState(store, { plannings: cached, loadingPlannings: false });
                return of(cached);
              }
            }

            // Fetch from API
            return from(
              analysisService.getAnalysisPlannings(organizationId, analysisId),
            ).pipe(
              tap((plans) => {
                // Cache the result
                cacheService.set(
                  'plannings',
                  cacheKey,
                  plans,
                  DEFAULT_CACHE_TTL,
                );

                // Retain unchanged planning objects so unrelated SSE updates do not
                // recreate the selected result, viewer or charts.
                const previous = new Map(store.plannings().map((p) => [p.id, p]));
                const stablePlans = plans.map((p) => {
                  const old = previous.get(p.id);
                  return old && JSON.stringify(old) === JSON.stringify(p) ? old : p;
                });
                patchState(store, { plannings: stablePlans, loadingPlannings: false });
                debugLog(
                  `[AnalysisDetailStore] Plannings loaded: ${plans.length} items`,
                );

                // Pre-fetch chart data for first planning
                const firstCompleted = plans.find(
                  (planning) => planning.status === 'completed',
                );
                if (firstCompleted) {
                  loadPlanningChartData(
                    organizationId,
                    analysisId,
                    firstCompleted.id,
                  );
                }
              }),
              catchError((err) => {
                const errorMessage =
                  err.message ||
                  'Fehler beim Laden der Planungen';
                debugLog(
                  '[AnalysisDetailStore] Error loading plannings:',
                  err,
                );
                patchState(store, { error: errorMessage, loadingPlannings: false });
                toastService.error(errorMessage);
                return of(null);
              }),
            );
          }),
        ),
      );

      /**
       * Load polygons with optional cache bypass
       */
      const loadPolygonsSection = rxMethod<{
        organizationId: string;
        analysisId: string;
        forceRefresh?: boolean;
      }>(
        pipe(
          tap(() => {
            patchState(store, { loadingPolygons: store.polygons() === null, error: null });
          }),
          switchMap(({ organizationId, analysisId, forceRefresh }) => {
            const cacheKey = `${organizationId}:${analysisId}`;

            // Check cache if not forcing refresh
            if (!forceRefresh) {
              const cached = cacheService.getOrNull<AnalysisPolygon[]>(
                'polygons',
                cacheKey,
              );
              if (cached) {
                patchState(store, { polygons: cached, loadingPolygons: false });
                return of(cached);
              }
            }

            // Fetch from API
            return from(
              analysisService.getAnalysisPolygons(organizationId, analysisId),
            ).pipe(
              tap((polys) => {
                // Cache the result
                cacheService.set(
                  'polygons',
                  cacheKey,
                  polys,
                  DEFAULT_CACHE_TTL,
                );

                patchState(store, { polygons: polys, loadingPolygons: false });
                debugLog(
                  `[AnalysisDetailStore] Polygons loaded: ${polys.length} polygons`,
                );
              }),
              catchError((err) => {
                const errorMessage =
                  err.message || 'Fehler beim Laden der Polygone';
                debugLog('[AnalysisDetailStore] Error loading polygons:', err);
                patchState(store, { error: errorMessage, loadingPolygons: false });
                toastService.error(errorMessage);
                return of(null);
              }),
            );
          }),
        ),
      );

      /**
       * Load chart data for a specific planning
       */
      const loadPlanningChartData = async (
        organizationId: string,
        analysisId: string,
        planningId: string,
      ): Promise<void> => {
        // Skip only if we already have chart data with actual content.
        // A cached entry with total=0 is not treated as a hit so that we
        // re-fetch once SolarInsight finishes generating the charts.
        const currentCache = store.planningChartDataCache();
        const cached = currentCache[planningId];
        if (cached && cached.total > 0) {
          debugLog(
            `[AnalysisDetailStore] Chart data already cached for ${planningId}`,
          );
          return;
        }

        patchState(store, { loadingChartData: true });

        try {
          const data = await analysisService.getPlanningChartData(
            organizationId,
            analysisId,
            planningId,
          );

          // Re-read the cache at write time so concurrent calls for different
          // plannings don't overwrite each other's entries via a stale snapshot.
          patchState(store, {
            planningChartDataCache: {
              ...store.planningChartDataCache(),
              [planningId]: data,
            },
            loadingChartData: false,
          });

          debugLog(
            `[AnalysisDetailStore] Chart data loaded for ${planningId}`,
          );
        } catch (err) {
          debugLog(
            '[AnalysisDetailStore] Error loading chart data:',
            err,
          );
          patchState(store, { loadingChartData: false });
        }
      };

      /**
       * Initialize analysis detail page:
       * 1. Load all sections in parallel (detail, files, plannings)
       * 2. Open SSE connection if pipeline is active
       */
      const initializeAnalysisDetail = async (params: {
        organizationId: string;
        analysisId: string;
      }): Promise<void> => {
        const { organizationId, analysisId } = params;

        debugLog(
          `[AnalysisDetailStore] Initializing analysis ${analysisId}`,
        );

        patchState(store, {
          lastLoadedId: analysisId,
          error: null,
          sseContext: { organizationId, analysisId },
          // Reset polygons so the lazy-load guard in analysis-detail.ts triggers
          // fresh on every page visit. The store is a root singleton — without this
          // reset, revisiting an analysis shows the previous polygon state indefinitely.
          polygons: null,
        });

        try {
          // Load initial sections (detail first, then files + plannings in parallel)
          loadDetailSection({ organizationId, analysisId });
          loadFilesSection({ organizationId, analysisId });
          loadPlanningsSection({ organizationId, analysisId });
        } catch (err) {
          const errorMessage =
            (err as Error).message ||
            'Fehler beim Laden der Analyse-Details';
          debugLog(
            '[AnalysisDetailStore] Error initializing:',
            err,
          );
          patchState(store, { error: errorMessage });
          toastService.error(errorMessage);
        }
      };

      /**
       * Handle SSE events and trigger cascade invalidation
       */
      const onSSEUpdate = (updatedAnalysis: Analysis): void => {
        const previousDetail = store.detail();
        if (!previousDetail) return;

        debugLog('[AnalysisDetailStore] SSE update received');

        // Selectively apply the fields the SSE payload is designed to carry.
        // A full spread would overwrite analysisId (Hub UUID) with the SI UUID
        // from the SSE payload, causing analysis-module-view and analysis-plannings
        // to treat every SSE tick as a new analysis and re-fetch or corrupt data.
        patchState(store, {
          detail: {
            ...previousDetail,
            businessStatus:
              updatedAnalysis.businessStatus ?? previousDetail.businessStatus,
            technicalStatus:
              updatedAnalysis.technicalStatus ?? previousDetail.technicalStatus,
            worthwhile:
              updatedAnalysis.worthwhile ?? previousDetail.worthwhile,
            updatedAt:
              updatedAnalysis.updatedAt ?? previousDetail.updatedAt,
            archivedAt:
              updatedAnalysis.archivedAt ?? previousDetail.archivedAt,
          },
        });

        // Determine cascade invalidation based on status changes
        const invalidationRequest: InvalidationRequest = {
          sections: [],
          reason: 'sse_update' as InvalidationReason,
        };

        const previousStatus = previousDetail.technicalStatus;
        const nextStatus = updatedAnalysis.technicalStatus;
        const statusChanged = !!nextStatus && (
          previousStatus?.state !== nextStatus.state ||
          previousStatus?.taskKey !== nextStatus.taskKey ||
          previousStatus?.stage !== nextStatus.stage
        );
        if (statusChanged) {
          invalidationRequest.sections.push('detail', 'files');
          const planningTasks = ['max_placement', 'planning'];
          if (planningTasks.includes(nextStatus?.taskKey ?? '') ||
              planningTasks.includes(previousStatus?.taskKey ?? '')) {
            invalidationRequest.sections.push('plannings');
          }
          // Detection/orientation output can arrive before the full pipeline completes.
          const polygonTasks = ['detection', 'orientation'];
          if (polygonTasks.includes(nextStatus?.taskKey ?? '') ||
              polygonTasks.includes(previousStatus?.taskKey ?? '')) {
            invalidationRequest.sections.push('polygons');
          }
          if (nextStatus?.stage === 'complete') {
            invalidationRequest.sections = ['detail', 'files', 'polygons', 'plannings'];
          }
        }

        // Trigger cascade invalidation if any sections need it
        if (invalidationRequest.sections.length > 0) {
          cascadeInvalidateSections(invalidationRequest);
        }
      };

      /**
       * Cascade invalidate multiple sections and reload them
       */
      const cascadeInvalidateSections = async (
        request: InvalidationRequest,
      ): Promise<void> => {
        debugLog(
          `[AnalysisDetailStore] Cascade invalidating sections: ${request.sections.join(', ')}`,
          `reason: ${request.reason}`,
        );

        // Mark sections as invalidated (invalidate cache)
        if (request.sections.includes('detail')) {
          cacheService.invalidate('detail');
        }
        if (request.sections.includes('files')) {
          cacheService.invalidate('files');
        }
        if (request.sections.includes('polygons')) {
          cacheService.invalidate('polygons');
        }
        if (request.sections.includes('plannings')) {
          cacheService.invalidate('plannings');
        }

        // Reload sections (async, no await - let UI update in background)
        const organizationId = store.sseContext()?.organizationId;
        const analysisId = store.lastLoadedId();

        if (organizationId && analysisId) {
          if (request.sections.includes('detail')) {
            loadDetailSection({
              organizationId,
              analysisId,
              forceRefresh: true,
            });
          }
          if (request.sections.includes('files')) {
            loadFilesSection({
              organizationId,
              analysisId,
              forceRefresh: true,
            });
          }
          if (request.sections.includes('polygons')) {
            loadPolygonsSection({
              organizationId,
              analysisId,
              forceRefresh: true,
            });
          }
          if (request.sections.includes('plannings')) {
            loadPlanningsSection({
              organizationId,
              analysisId,
              forceRefresh: true,
            });
          }
        }
      };

      /**
       * Connect SSE and set up event listener
       */
      const connectSse = (params: {
        organizationId: string;
        analysisId: string;
      }): void => {
        disconnectSse();

        patchState(store, { polling: true, sseContext: params });

        // The transport owns bounded reconnects. Job duration is not a stream
        // timeout; unsubscribing here also aborts the underlying fetch.
        sseSubscription = analysisService
          .streamAnalysisEvents(params.organizationId, params.analysisId)
          .subscribe({
            next: (event) => {
              onSSEUpdate(event);
              patchState(store, {
                lastSyncAt: new Date().toISOString(),
              });

              // Stop polling if pipeline completes
              const detail = store.detail();
              if (detail && !isTechnicallyActive(detail)) {
                disconnectSse();
              }
            },
            error: (err) => {
              debugLog('[AnalysisDetailStore] SSE error:', err);
              const message = 'Live-Status nicht erreichbar. Die Berechnung kann weiterlaufen; bitte die Analyse synchronisieren oder die Seite neu laden.';
              patchState(store, { polling: false, error: message });
              toastService.error(message);
            },
            complete: () => {
              debugLog('[AnalysisDetailStore] SSE completed');
              patchState(store, { polling: false });
            },
          });
      };

      // Auto-connect SSE once detail data arrives and the page is visible.
      effect(() => {
        const detail = store.detail();
        const ctx = store.sseContext();
        const visible = store.pageVisible();

        if (!detail || !ctx || !visible) return;
        if (store.polling() || sseSubscription) return;

        if (isTechnicallyActive(detail) || isPendingActivation(detail)) {
          connectSse(ctx);
        }
      });

      /**
       * Disconnect SSE
       */
      const disconnectSse = (): void => {
        if (sseSubscription) {
          sseSubscription.unsubscribe();
          sseSubscription = null;
        }
        patchState(store, { polling: false, sseContext: null });
      };

      /**
       * Manual sync - force refresh from SI API
       */
      const syncAnalysis = async (): Promise<void> => {
        const organizationId = store.sseContext()?.organizationId;
        const analysisId = store.lastLoadedId();

        if (!organizationId || !analysisId) {
          toastService.error('Erforderliche Daten fehlen');
          return;
        }

        try {
          patchState(store, { error: null });
          loadDetailSection({
            organizationId,
            analysisId,
            forceRefresh: true,
          });
          toastService.success('Analyse synchronisiert');
          patchState(store, {
            lastSyncAt: new Date().toISOString(),
          });
        } catch (error) {
          debugLog('[AnalysisDetailStore] Sync failed:', error);
          toastService.error('Synchronisierung fehlgeschlagen');
        }
      };

      // Public API - METHODS ONLY (withMethods constraint)
      return {
        initializeAnalysisDetail,
        connectSse,
        disconnectSse,
        syncAnalysis,
        cascadeInvalidateSections,

        updateAnalysisOptimistically: (update: Partial<Analysis>) => {
          const current = store.detail();
          if (!current) return;
          const previous = { ...current };
          patchState(store, { detail: { ...current, ...update } });
          return { rollback: () => patchState(store, { detail: previous }) };
        },

        updateSelectedMedia: (update: {
          selectedMediaId?: string | null;
          selectedMediaType?: '2d' | '3d' | null;
        }) => {
          patchState(store, {
            selectedMediaId: update.selectedMediaId || undefined,
            selectedMediaType: update.selectedMediaType || undefined,
          });
        },

        setPageVisible: (visible: boolean) => {
          patchState(store, { pageVisible: visible });
          if (visible && store.polling() === false) {
            const detail = store.detail();
            if (detail) {
              if (
                isTechnicallyActive(detail) ||
                isPendingActivation(detail)
              ) {
                connectSse(store.sseContext()!);
              }
            }
          }
        },

        clearCurrentAnalysis: () => {
          disconnectSse();
          patchState(store, {
            detail: null,
            filesOverview: [],
            plannings: [],
            polygons: null,
            lastLoadedId: null,
            error: null,
            lastSyncAt: null,
            planningChartDataCache: {},
          });
          cacheService.invalidateAll();
        },

        loadFilesOverview: (params: {
          organizationId: string;
          analysisId: string;
          forceRefresh?: boolean;
        }) => loadFilesSection(params),

        loadPolygons: (params: {
          organizationId: string;
          analysisId: string;
          forceRefresh?: boolean;
        }) => loadPolygonsSection(params),

        loadPlannings: (params: {
          organizationId: string;
          analysisId: string;
        }) => loadPlanningsSection(params),

        loadPlanningChartData: async (
          organizationId: string,
          analysisId: string,
          planningId: string,
        ) => {
          return loadPlanningChartData(
            organizationId,
            analysisId,
            planningId,
          );
        },

        reloadCurrentAnalysis: (organizationId: string) => {
          const analysisId = store.lastLoadedId();
          if (analysisId) {
            loadDetailSection({
              organizationId,
              analysisId,
              forceRefresh: true,
            });
          }
        },

        // Additional methods for backward compatibility
        reloadPolygonsAsync: async (organizationId: string) => {
          const analysisId = store.lastLoadedId();
          if (!analysisId) return;
          loadPolygonsSection({ organizationId, analysisId, forceRefresh: true });
        },

        // Backward compatibility aliases
        startPolling: (params: { organizationId: string; analysisId: string }) =>
          connectSse(params),

        stopPolling: () => disconnectSse(),

        // API methods that update store state
        deletePlanning: async (params: {
          organizationId: string;
          analysisId: string;
          planningId: string;
        }) => {
          const { organizationId, analysisId, planningId } = params;
          try {
            await analysisService.deletePlanning(
              organizationId,
              analysisId,
              planningId,
            );
            cacheService.invalidate('plannings');
            cacheService.invalidate('detail');
            cacheService.invalidate('files');
            // Remove immediately after the server confirms, then await authoritative state.
            if (store.lastLoadedId() === analysisId) {
              patchState(store, { plannings: store.plannings().filter(plan => plan.id !== planningId), planningChartDataCache: {} });
            }
            try {
              const [plans, detail, files] = await Promise.all([
                analysisService.getAnalysisPlannings(organizationId, analysisId),
                analysisService.fetchAnalysisDetail(organizationId, analysisId, { sync: true }),
                analysisService.getFilesOverview(organizationId, analysisId),
              ]);
              if (store.lastLoadedId() !== analysisId) return true;
              cacheService.set('plannings', `${organizationId}:${analysisId}`, plans, DEFAULT_CACHE_TTL);
              cacheService.set('detail', `${organizationId}:${analysisId}`, detail, DEFAULT_CACHE_TTL);
              patchState(store, { plannings: plans, detail, filesOverview: files });
              return true;
            } catch (error) {
              debugLog('[AnalysisDetailStore] Planning deleted, but refresh failed:', error);
              return false;
            }
          } catch (err) {
            debugLog('[AnalysisDetailStore] Error deleting planning:', err);
            throw err;
          }
        },

        setPrimaryPlanning: async (params: {
          organizationId: string;
          analysisId: string;
          planningId: string;
        }) => {
          const { organizationId, analysisId, planningId } = params;
          try {
            await analysisService.setPrimaryPlanning(
              organizationId,
              analysisId,
              planningId,
            );
            const detail = store.detail();
            if (detail) {
              patchState(store, {
                detail: { ...detail, primaryPlanningId: planningId },
              });
            }
          } catch (err) {
            debugLog(
              '[AnalysisDetailStore] Error setting primary planning:',
              err,
            );
            throw err;
          }
        },
      };
    },
  ),

  withHooks({
    onDestroy(store) {
      debugLog('[AnalysisDetailStore] Cleaning up on destroy');
      (store as any).disconnectSse();
      (store as any).clearCurrentAnalysis();
    },
  }),
);
